import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, DoorOpen, ExternalLink, KeyRound, Pencil } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { RsvpTable } from "../components/RsvpTable";
import { EmailGifPanel } from "../components/EmailGifPanel";
import { DoorGuestSection } from "../components/DoorGuestSection";
import { CohostSection } from "../components/CohostSection";
import { PageLoader } from "../context/AuthContext";
import { api, copyText, formatApiError, shareUrl } from "../lib/api";
import { formatDate, formatTime } from "../lib/format";

const FILTERS = [["all", "All"], ["yes", "Attending"], ["maybe", "Maybe"], ["no", "Declined"]];

const Stat = ({ label, value, cls, testid }) => (
  <div className={`rounded-3xl p-6 ${cls}`}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">{label}</p>
    <p data-testid={testid} className="font-display mt-3 text-4xl font-extrabold tracking-tight">{value}</p>
  </div>
);

const pill = "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-[background-color] duration-200 hover:bg-slate-900 hover:text-white";

export default function EventAdmin() {
  const { id } = useParams();
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [doorKey, setDoorKey] = useState(0);

  useEffect(() => {
    const params = filter === "all" ? {} : { response: filter };
    api.get(`/events/${id}/rsvps`, { params }).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id, filter, reloadKey]);

  if (error) return (
    <AppShell>
      <div className="text-center">
        <p data-testid="admin-error" className="text-slate-600">{error}</p>
        <Link to="/dashboard" data-testid="admin-error-back-link" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800"><ArrowLeft className="h-4 w-4" /> Back to your parties</Link>
      </div>
    </AppShell>
  );
  if (!data) return <AppShell><PageLoader /></AppShell>;

  const { event, stats, rsvps } = data;
  const link = shareUrl(event.slug);
  const copy = async () => {
    await copyText(link);
    toast.success("Invite link copied");
  };
  const copyWithPassword = async () => {
    await copyText(`${link}\nPassword: ${event.invite_password}`);
    toast.success("Link and password copied");
  };

  return (
    <AppShell>
      <Link to="/dashboard" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> All parties
      </Link>
      <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 data-testid="admin-event-title" className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{event.occasion}</h1>
          <p className="mt-2 text-slate-500">{[formatDate(event.date), formatTime(event.time), event.place].filter(Boolean).join(" · ") || "Details TBA"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`/e/${event.slug}`} target="_blank" rel="noopener noreferrer" data-testid="admin-open-invite-btn" className={pill}><ExternalLink className="h-4 w-4" /> View invite</a>
          <Link to={`/events/${event.id}/edit`} data-testid="admin-edit-btn" className={pill}><Pencil className="h-4 w-4" /> Edit</Link>
          <Link to={`/events/${event.id}/door`} data-testid="admin-door-btn" className={pill}><DoorOpen className="h-4 w-4" /> Door check-in</Link>
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-3 rounded-3xl bg-slate-900 p-4 text-white sm:flex-row sm:items-center sm:p-5">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Share link</span>
        <code data-testid="admin-share-link" className="flex-1 truncate font-mono text-sm text-slate-200">{link}</code>
        <button data-testid="admin-copy-link-btn" onClick={copy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 py-2 text-sm font-semibold text-white transition-[background-color] duration-200 hover:bg-blue-600 hover:text-white">
          <Copy className="h-4 w-4" /> Copy
        </button>
      </div>
      {event.password_enabled && (
        <div data-testid="admin-password-bar" className="mt-3 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:p-5">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700"><KeyRound className="h-4 w-4" /> Password</span>
          <code data-testid="admin-invite-password" className="flex-1 truncate font-mono text-sm text-slate-800">{event.invite_password}</code>
          <button data-testid="admin-copy-link-password-btn" onClick={copyWithPassword} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 transition-[background-color] duration-200 hover:bg-slate-50">
            <Copy className="h-4 w-4" /> Copy link + password
          </button>
        </div>
      )}
      <EmailGifPanel event={event} />
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Headcount attending" value={stats.headcount} cls="bg-blue-700 text-white col-span-2 lg:col-span-1" testid="admin-headcount" />
        <Stat label="Yes" value={stats.yes} cls="bg-emerald-50 text-emerald-800" testid="admin-count-yes" />
        <Stat label="Maybe" value={stats.maybe} cls="bg-amber-50 text-amber-800" testid="admin-count-maybe" />
        <Stat label="No" value={stats.no} cls="bg-white text-slate-800 border border-slate-200" testid="admin-count-no" />
      </div>
      <div className="mb-5 mt-12 flex flex-wrap gap-2" role="tablist">
        {FILTERS.map(([v, label]) => (
          <button
            key={v}
            role="tab"
            aria-selected={filter === v}
            data-testid={`admin-filter-${v}`}
            onClick={() => setFilter(v)}
            className={`rounded-full px-5 py-2 text-sm font-bold transition-[background-color,color] duration-200 ${filter === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
          >
            {label} {v === "all" ? stats.total : stats[v]}
          </button>
        ))}
      </div>
      <RsvpTable eventId={event.id} rsvps={rsvps} doorGuests={data.door_guests || []} onChanged={() => setReloadKey((k) => k + 1)} onDoorChanged={() => setDoorKey((k) => k + 1)} />
      <DoorGuestSection eventId={event.id} reloadKey={doorKey} onChanged={() => setReloadKey((k) => k + 1)} />
      <CohostSection eventId={event.id} />
    </AppShell>
  );
}
